import { useEffect, useState } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { PageHeader, Empty } from "@/components/PageHeader";
import { api, apiError } from "@/lib/api";
import { GST_RATES, money } from "@/lib/format";
import { MagnifyingGlass, Trash } from "@phosphor-icons/react";

const EMPTY = {
  name: "", sku: "", hsn: "", uom: "NOS", gst_rate: 18, sale_price: 0, purchase_price: 0,
  opening_qty: 0, reorder_level: 0, item_group: "General",
};

export default function Items() {
  const [rows, setRows] = useState([]);
  const [q, setQ] = useState("");
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(EMPTY);
  const [hsnQuery, setHsnQuery] = useState("");
  const [hsnResults, setHsnResults] = useState([]);

  const load = () => api.get("/items").then((r) => setRows(r.data));
  useEffect(() => { load(); }, []);

  const searchHsn = async () => {
    const { data } = await api.get(`/hsn/search?q=${encodeURIComponent(hsnQuery || form.name)}`);
    setHsnResults(data);
    if (!data.length) toast.info("No HSN suggestion — enter manually");
  };

  const create = async () => {
    try {
      await api.post("/items", {
        ...form, gst_rate: Number(form.gst_rate), sale_price: Number(form.sale_price),
        purchase_price: Number(form.purchase_price), opening_qty: Number(form.opening_qty),
        reorder_level: Number(form.reorder_level),
      });
      toast.success("Item created");
      setOpen(false); setForm(EMPTY); setHsnResults([]); load();
    } catch (e) { toast.error(apiError(e)); }
  };

  const remove = async (id) => {
    try { await api.delete(`/items/${id}`); toast.success("Item deleted"); load(); }
    catch (e) { toast.error(apiError(e)); }
  };

  const filtered = rows.filter((r) => !q || r.name.toLowerCase().includes(q.toLowerCase()) || (r.hsn || "").includes(q));

  return (
    <div className="space-y-6" data-testid="items-page">
      <PageHeader title="Items & HSN" subtitle="Stock and service items with HSN/SAC classification and GST rate defaults.">
        <Input placeholder="Search item or HSN…" value={q} onChange={(e) => setQ(e.target.value)}
          className="h-8 w-56 text-sm" data-testid="item-search-input" />
        <Dialog open={open} onOpenChange={setOpen}>
          <DialogTrigger asChild><Button size="sm" data-testid="new-item-btn">New item</Button></DialogTrigger>
          <DialogContent className="max-w-xl" data-testid="item-dialog">
            <DialogHeader><DialogTitle className="font-display">Create item</DialogTitle></DialogHeader>
            <div className="grid gap-4 sm:grid-cols-2">
              <div className="space-y-1.5 sm:col-span-2">
                <Label className="text-xs uppercase tracking-widest">Item name</Label>
                <Input data-testid="item-name-input" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} />
              </div>
              <div className="space-y-1.5 sm:col-span-2">
                <Label className="text-xs uppercase tracking-widest">HSN / SAC — AI suggest</Label>
                <div className="flex gap-2">
                  <Input className="num" data-testid="item-hsn-input" value={form.hsn}
                    onChange={(e) => setForm({ ...form, hsn: e.target.value })} placeholder="8471" />
                  <Input className="text-sm" placeholder="describe the goods…" value={hsnQuery}
                    onChange={(e) => setHsnQuery(e.target.value)} data-testid="hsn-query-input" />
                  <Button type="button" variant="outline" size="sm" onClick={searchHsn} data-testid="hsn-search-btn">
                    <MagnifyingGlass size={14} />
                  </Button>
                </div>
                {hsnResults.length > 0 && (
                  <div className="max-h-32 overflow-y-auto border border-border" data-testid="hsn-results">
                    {hsnResults.map((h) => (
                      <button key={h.hsn} type="button"
                        onClick={() => setForm({ ...form, hsn: h.hsn, gst_rate: h.gst_rate })}
                        data-testid={`hsn-option-${h.hsn}`}
                        className="flex w-full items-center justify-between px-2 py-1.5 text-left text-xs hover:bg-accent">
                        <span><span className="num font-medium">{h.hsn}</span> · {h.description}</span>
                        <span className="num text-muted-foreground">{h.gst_rate}%</span>
                      </button>
                    ))}
                  </div>
                )}
              </div>
              <div className="space-y-1.5">
                <Label className="text-xs uppercase tracking-widest">GST rate</Label>
                <Select value={String(form.gst_rate)} onValueChange={(v) => setForm({ ...form, gst_rate: v })}>
                  <SelectTrigger data-testid="item-gst-select"><SelectValue /></SelectTrigger>
                  <SelectContent>{GST_RATES.map((r) => <SelectItem key={r} value={String(r)}>{r}%</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div className="space-y-1.5">
                <Label className="text-xs uppercase tracking-widest">UOM</Label>
                <Input data-testid="item-uom-input" value={form.uom} onChange={(e) => setForm({ ...form, uom: e.target.value })} />
              </div>
              <div className="space-y-1.5">
                <Label className="text-xs uppercase tracking-widest">Sale price</Label>
                <Input type="number" className="num" data-testid="item-sale-price-input" value={form.sale_price}
                  onChange={(e) => setForm({ ...form, sale_price: e.target.value })} />
              </div>
              <div className="space-y-1.5">
                <Label className="text-xs uppercase tracking-widest">Purchase price</Label>
                <Input type="number" className="num" data-testid="item-purchase-price-input" value={form.purchase_price}
                  onChange={(e) => setForm({ ...form, purchase_price: e.target.value })} />
              </div>
              <div className="space-y-1.5">
                <Label className="text-xs uppercase tracking-widest">Opening qty</Label>
                <Input type="number" className="num" data-testid="item-opening-qty-input" value={form.opening_qty}
                  onChange={(e) => setForm({ ...form, opening_qty: e.target.value })} />
              </div>
              <div className="space-y-1.5">
                <Label className="text-xs uppercase tracking-widest">Reorder level</Label>
                <Input type="number" className="num" data-testid="item-reorder-input" value={form.reorder_level}
                  onChange={(e) => setForm({ ...form, reorder_level: e.target.value })} />
              </div>
            </div>
            <DialogFooter><Button onClick={create} disabled={!form.name} data-testid="save-item-btn">Create</Button></DialogFooter>
          </DialogContent>
        </Dialog>
      </PageHeader>

      {filtered.length === 0 ? (
        <Empty label="No items yet" hint="Create an item with HSN to start invoicing." />
      ) : (
        <div className="overflow-x-auto border border-border">
          <table className="dense-table w-full min-w-[860px]" data-testid="items-table">
            <thead>
              <tr><th>Item</th><th>HSN/SAC</th><th>UOM</th><th className="text-right">GST</th>
                <th className="text-right">Sale price</th><th className="text-right">Purchase</th>
                <th className="text-right">Reorder</th><th /></tr>
            </thead>
            <tbody>
              {filtered.map((it) => (
                <tr key={it.id} className="row-in" data-testid={`item-row-${it.id}`}>
                  <td className="font-medium">{it.name}</td>
                  <td className="num">{it.hsn || "—"}</td>
                  <td className="text-muted-foreground">{it.uom}</td>
                  <td className="num text-right">{it.gst_rate}%</td>
                  <td className="num text-right">{money(it.sale_price)}</td>
                  <td className="num text-right text-muted-foreground">{money(it.purchase_price)}</td>
                  <td className="num text-right text-muted-foreground">{it.reorder_level || "—"}</td>
                  <td className="text-right">
                    <button onClick={() => remove(it.id)} data-testid={`delete-item-${it.id}`} className="text-muted-foreground hover:text-destructive">
                      <Trash size={14} />
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
